import { Trans, useTranslation } from "react-i18next";
import LeafTile from "../../../common/leaf/LeafTile/LeafTile";
import defaultContent from "../../../content/ssi";
import commonDefaultContent from "../../../content/common";
import { SSIEligibility, SSIEligibilityMonth } from "../../../services/StrongStart/ssiAPI.types";
import LeafTileDivider from "../../../common/leaf/LeafTileDivider/LeafTileDivider";
import {
  getLongPeriodFormat,
  getPerformanceYear,
  getSelectedPerformanceYearData,
  getShortMonth,
  getYear,
} from "../../../common/util/period";
import { storedLocale } from "../../Authentication/util";
import { BADGE_SIZE, BADGE_TYPE, IconShield } from "../../../common/icons/IconShield/IconShield";
import "./SSIEligibilityDetails.scss";

type Props = {
  isLoading: boolean;
  eligibilityData: SSIEligibility;
  legendBadges: BADGE_TYPE[];
};

const SSIEligibilityDetails = ({ isLoading, eligibilityData, legendBadges }: Props) => {
  const locale = storedLocale();
  const { t } = useTranslation(["common", "ssi"]);
  const hasStarted = eligibilityData?.StartPeriod;
  const allMonths = [...(eligibilityData.CurrentYear || []), ...(eligibilityData.NextYear || [])];
  const splitHistoryByYear: { [key: string]: SSIEligibilityMonth[] } = {};
  for (const month of allMonths) {
    const year = getPerformanceYear(month.Period.toString());
    splitHistoryByYear[year] = splitHistoryByYear[year] ? [...splitHistoryByYear[year], month] : [month];
  }

  const years = Object.keys(splitHistoryByYear);

  const getBadgeTitle = (badgeType: string) => {
    return t(`${badgeType}`, commonDefaultContent[badgeType]).toString();
  };

  const renderGhostElements = (months: number) => {
    const numGhostElements = (4 - (months % 4)) % 4;
    const ghostElements = [];
    for (let i = 0; i < numGhostElements; i++) {
      ghostElements.push(<div key={i} className="SSI__eligibility-modal__grid-container__month"></div>);
    }
    return <>{ghostElements}</>;
  };

  const renderBadgeGrid = (periodList: SSIEligibilityMonth[]) => {
    return (
      <>
        {periodList.map((month) => {
          return (
            <div key={month.Period} className="SSI__eligibility-modal__grid-container__month">
              <div>
                <IconShield
                  type={month.BadgeType as BADGE_TYPE}
                  title={getBadgeTitle(month.BadgeType)}
                  size={BADGE_SIZE.MEDIUM_LARGE}
                ></IconShield>
              </div>
              <div>{getShortMonth(month.Period.toString(), locale)}</div>
            </div>
          );
        })}
        {renderGhostElements(periodList.length)}
      </>
    );
  };

  return (
    <section id="ssi-eligibility-details">
      <LeafTile isLoading={isLoading}>
        <p className="SSI__eligibility-modal__description">
          {hasStarted ? (
            <Trans
              i18nKey={"eligibilityModal.eligibilityDescriptionEveryMonth"}
              ns="ssi"
              defaults={defaultContent.eligibilityModal.eligibilityDescriptionEveryMonth}
              components={{
                1: <strong></strong>,
              }}
            ></Trans>
          ) : (
            <Trans
              i18nKey={"eligibilityModal.eligibilityDescriptionSecondFullMonth"}
              ns="ssi"
              defaults={defaultContent.eligibilityModal.eligibilityDescriptionSecondFullMonth}
              components={{
                1: <strong></strong>,
              }}
            ></Trans>
          )}
        </p>
        <div className="SSI__eligibility-modal__date">
          <strong>
            <Trans>{t("eligibilityModal.entryDate", defaultContent.eligibilityModal.entryDate, { ns: "ssi" })}</Trans>
            :&nbsp;
          </strong>
          {eligibilityData.EntryPeriod ? getLongPeriodFormat(eligibilityData.EntryPeriod, locale) : ""}
        </div>
        {eligibilityData.StartPeriod ? (
          <div className="SSI__eligibility-modal__date">
            <strong>
              <Trans>{t("eligibilityModal.startDate", defaultContent.eligibilityModal.startDate, { ns: "ssi" })}</Trans>
              :&nbsp;
            </strong>
            {getLongPeriodFormat(eligibilityData.StartPeriod, locale)}
          </div>
        ) : (
          <></>
        )}
        {eligibilityData.EndPeriod ? (
          <div className="SSI__eligibility-modal__date">
            <strong>
              <Trans>{t("eligibilityModal.endDate", defaultContent.eligibilityModal.endDate, { ns: "ssi" })}</Trans>
              :&nbsp;
            </strong>
            {getLongPeriodFormat(eligibilityData.EndPeriod, locale)}
          </div>
        ) : (
          <></>
        )}
        {years.map((year) => {
          const { selectedPYEndPeriod } = getSelectedPerformanceYearData(String(splitHistoryByYear[year][0].Period));
          const formattedYear = getYear(selectedPYEndPeriod, locale).slice(2, 4);
          return (
            <div key={year}>
              <h2>
                <p className="pyTitle">{`${t(
                  "performanceYearShort",
                  commonDefaultContent.performanceYearShort as string,
                  {
                    ns: "common",
                  },
                )}${formattedYear}`}</p>
              </h2>
              <div className="SSI__eligibility-modal__grid-container">{renderBadgeGrid(splitHistoryByYear[year])}</div>
            </div>
          );
        })}

        <div className="SSI__eligibility-modal__divider">
          <LeafTileDivider></LeafTileDivider>
        </div>
        <div
          className={`SSI__eligibility-modal__badge-legend SSI__eligibility-modal__badge-legend--count-${legendBadges.length}`}
        >
          {(legendBadges || []).map((badge) => (
            <div key={badge} className="SSI__eligibility-modal__badge-legend__item">
              <IconShield type={badge} title={getBadgeTitle(badge)} />
              <div className="SSI__eligibility-modal__badge-legend__item__title">{getBadgeTitle(badge)}</div>
            </div>
          ))}
        </div>
      </LeafTile>
    </section>
  );
};

export default SSIEligibilityDetails;
